import { Alert, Button, Input, Spin, Typography } from 'antd';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import JoinProductAd from '@/assets/images/page/join-product-ad.png';
import { BASE_NAME } from '@/infrastructure/history';
import { IGetInvitationDetailRsp, productUserService } from '@/service/product-user';
import styles from './index.module.scss';

const { Title, Paragraph } = Typography;

/** 从邀请链接中解析 token：仅支持完整邀请链接（含 ?token=xxx），非链接一律视为无效。 */
const parseToken = (input: string): string => {
  const value = input.trim();
  if (!value) {
    return '';
  }
  const match = value.match(/[?&]token=([^&#\s]+)/);
  if (match) {
    try {
      return decodeURIComponent(match[1]);
    } catch {
      return match[1];
    }
  }
  return '';
};

/** JoinProduct 加入产品页面：支持邀请链接（/join?token=xxx）或手动输入邀请链接/邀请码加入产品。 */
const JoinProduct: FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const urlToken = useMemo(
    () => new URLSearchParams(location.search).get('token') ?? '',
    [location.search],
  );
  const urlProductId = useMemo(
    () => new URLSearchParams(location.search).get('productId') ?? '',
    [location.search],
  );

  // URL 带 token 时进页面，输入框默认回填当前完整邀请链接（而非裸 token），与"仅允许邀请链接"一致。
  const [inputValue] = useState(urlToken ? window.location.href : '');
  const [detail, setDetail] = useState<IGetInvitationDetailRsp | null>(null);
  const [loading, setLoading] = useState(Boolean(urlToken));
  const [joining, setJoining] = useState(false);
  const [errorTitle, setErrorTitle] = useState('');
  const [errorDesc, setErrorDesc] = useState('');
  const [errorVisible, setErrorVisible] = useState(false);

  const showError = (title: string, desc?: string) => {
    setErrorTitle(title);
    setErrorDesc(desc ?? '');
    setErrorVisible(true);
  };

  // URL 带 token 时进页面自动校验邀请详情（预填输入框 + 提前提示过期/失效）
  useEffect(() => {
    if (!urlToken) {
      return;
    }
    setLoading(true);
    // urlToken 已是 query 里提取出的 token 值，直接用；不能再走 parseToken（那只认完整链接，裸 token 会被判空）。
    productUserService
      .getInvitationDetail({ token: urlToken })
      .then((d) => {
        setDetail(d);
        if (d?.status === 1) {
          showError(t('邀请链接已过期'), t('邀请链接已过期，请联系团队管理员获取新的邀请链接。'));
        } else if (d?.status === 2) {
          showError(t('邀请席位已用完'), t('邀请席位已用完，请联系团队管理员获取新的邀请链接。'));
        }
      })
      .catch(() => showError(t('邀请链接无效'), t('请检查邀请链接是否正确。')))
      .finally(() => setLoading(false));
  }, [urlToken]);

  const isMember = detail?.isMember === true;

  const gotoProduct = (productId: string) => {
    window.location.href = `${BASE_NAME}/overview?productId=${productId}`;
  };

  const handleJoin = async () => {
    const token = parseToken(inputValue);
    if (!token) {
      showError(t('请输入有效的邀请链接'));
      return;
    }
    setErrorVisible(false);
    setJoining(true);
    try {
      // productId 优先级：URL query > 已有 detail（URL 带 token 时进页面已拉过）> 现拉一次。
      // token 自解释：GetInvitationDetail 仅凭 token 即可反查出 productId，无需从链接解析。
      let productId = urlProductId || detail?.productId;
      if (!productId) {
        const d = await productUserService.getInvitationDetail({ token });
        setDetail(d);
        productId = d?.productId ?? undefined;
      }
      // joinByInvitation 需要 productId 落在 X-Productid header 上，用上一步换来的值显式指定。
      const config = productId ? { headers: { 'X-productid': productId } } : undefined;
      await productUserService.joinByInvitation({ token }, config);
      if (productId) {
        // 延迟 500ms 再跳，避免后端数据还没落库导致首页拉取异常
        await new Promise(resolve => setTimeout(resolve, 500));
        gotoProduct(productId);
      } else {
        showError(t('加入成功，但未获取到产品信息'), t('请稍后手动进入产品。'));
      }
    } catch {
      showError(t('加入失败'), t('请稍后重试。'));
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return <Spin style={{ marginTop: 120, display: 'block', textAlign: 'center' }} />;
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.formPanel}>
          <Title level={2} className={styles.title}>
            {t('加入产品')}
          </Title>
          <Paragraph className={styles.subtitle}>
            {t('通过邀请链接加入现有产品 {{productName}}，开始协作监控应用质量。', { productName: detail?.productName ?? '' })}
          </Paragraph>

          {isMember ? (
            <Alert
              className={styles.alert}
              type="success"
              showIcon
              message={t('已加入该产品')}
              description={t('你已经是 {{productName}} 的成员，可直接访问产品。', { productName: detail?.productName ?? t('该产品') })}
            />
          ) : (
            errorVisible && errorTitle && (
              <Alert
                className={styles.alert}
                type="error"
                showIcon
                closable
                onClose={() => setErrorVisible(false)}
                message={errorTitle}
                description={errorDesc || undefined}
              />
            )
          )}

          <div className={styles.label}>
            {t('邀请链接')}<span className={styles.required}>*</span>
          </div>
          <Input
            className={styles.input}
            value={inputValue}
            disabled
          />
          <div className={styles.help}>{t('当前邀请链接，确认后点击下方按钮加入')}</div>

          {isMember ? (
            <Button
              className={styles.submit}
              onClick={() => detail?.productId && gotoProduct(detail.productId)}
            >
              {t('访问产品')}
            </Button>
          ) : (
            <Button className={styles.submit} loading={joining} onClick={handleJoin}>
              {t('加入产品')}
            </Button>
          )}
        </div>

        <div className={styles.adPanel}>
          <img className={styles.adImage} src={JoinProductAd} alt={t('Bugly AI 全链路智能质量监控')} />
        </div>
      </div>
    </div>
  );
};

export default JoinProduct;
